import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { PHONE_E164_PATTERN } from "@handoverpro/shared";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { fetchProfile, updateProfilePhone } from "~/lib/profile";

// Local form schema: an empty field is allowed (it clears the number); a
// non-empty value must be E.164. Mirrors the shared `updateProfileSchema`'s
// empty-means-null rule. Kept as a single trimmed string so the input binds
// directly, with the empty→null mapping done on submit.
const profileFormSchema = z.object({
  phoneE164: z
    .string()
    .trim()
    .refine((value) => value === "" || PHONE_E164_PATTERN.test(value), {
      message: "Enter a phone number in international format, e.g. +447700900123",
    }),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

type LoadState = "loading" | "ready" | "error";

/**
 * Profile screen (V2-E6.3, HAN-66) — lets a doctor set or clear the phone number
 * a colleague can text a handover code to. Account-level (not per-device, unlike
 * notification settings): the number lives on the user row and is read by the
 * SMS recipient picker. Clearing the field removes the number.
 */
export function ProfileScreen(): JSX.Element {
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: { phoneE164: "" },
    mode: "onBlur",
  });

  // Prefill from the current profile.
  useEffect(() => {
    let cancelled = false;
    void fetchProfile().then((result) => {
      if (cancelled) return;
      if (result.status === "ok") {
        form.reset({ phoneE164: result.data.phoneE164 ?? "" });
        setLoadState("ready");
      } else {
        setLoadState("error");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [form]);

  const onSubmit = async (values: ProfileFormValues): Promise<void> => {
    const phone = values.phoneE164.trim() === "" ? null : values.phoneE164.trim();
    const result = await updateProfilePhone(phone);
    if (result.status === "ok") {
      form.reset({ phoneE164: result.data.phoneE164 ?? "" });
      toast.success(phone ? "Phone number saved." : "Phone number removed.");
    } else if (result.status === "invalid") {
      form.setError("phoneE164", {
        message: "Enter a phone number in international format, e.g. +447700900123",
      });
    } else {
      toast.error("Couldn't save your phone number. Please try again.");
    }
  };

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Profile</h1>
        <p className="text-sm text-muted-foreground">Manage your account details.</p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-muted-foreground" aria-hidden />
            Phone number
          </CardTitle>
          <CardDescription>
            Add a mobile number so a colleague can text you a handover code. Leave it blank to
            remove it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadState === "loading" && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading your profile…
            </p>
          )}

          {loadState === "error" && (
            <Alert variant="destructive">
              <AlertDescription>Couldn&rsquo;t load your profile. Please refresh.</AlertDescription>
            </Alert>
          )}

          {loadState === "ready" && (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="phoneE164"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mobile number</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          placeholder="+447700900123"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        International format, starting with + and your country code.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  Save
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
